import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Radio, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useToast } from 'use-toast-mui';
import { getLessonSettings, saveLessonSettings } from '../../../api/center-branch';
import { errorMessages } from '../../../utils/methods';

const GroupLessonSettings = ({ roomListInfo, setRoomListInfo, branchId, getDetailCenter, branchDetailInfo }) => {
  const { t } = useTranslation();
  const user = useSelector(state => state.user.user);
  const toast = useToast();

  // 예약 가능 시간 설정
  const [reservationAvailable, setReservationAvailable] = useState('Y');
  const [reservationCustomValue, setReservationCustomValue] = useState('');

  // 예약 취소 가능 시간 설정
  const [cancelAvailable, setCancelAvailable] = useState('Y');
  const [cancelCustomValue, setCancelCustomValue] = useState('');

  // 결석 시 수강권 차감 설정
  const [absenceDeduction, setAbsenceDeduction] = useState('Y');

  // 대기자 자동예약 설정
  const [waitlistAutoReservation, setWaitlistAutoReservation] = useState('Y');
  const [waitlistAutoReservationCustomValue, setWaitlistAutoReservationCustomValue] = useState('');

  // 데이터 로딩
  const fetchSettings = async () => {
    try {
      if (!branchId) return;
      const response = await getLessonSettings(branchId, 'GROUP_LESSON', user.token);
      if (response) {
        setReservationAvailable(response.reservationAvailableYn || 'Y');
        setReservationCustomValue(response.reservationAvailableHour?.toString() || '');
        setCancelAvailable(response.cancelAvailableYn || 'Y');
        setCancelCustomValue(response.cancelAvailableHour?.toString() || '');
        setAbsenceDeduction(response.absenceDeductionYn || 'Y');
        setWaitlistAutoReservation(response.waitlistAutoReservationYn || 'Y');
        setWaitlistAutoReservationCustomValue(response.waitlistAutoReservationHour?.toString() || '');
      }
    } catch (error) {
      console.error('Failed to fetch lesson settings:', error);
    }
  };

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  const handleSave = async () => {
    try {
      const data = {
        branchId: branchId,
        courseClassType: 'GROUP_LESSON',
        reservationAvailableYn: reservationAvailable,
        reservationAvailableHour: reservationAvailable === 'N' ? parseInt(reservationCustomValue) || null : null,
        cancelAvailableYn: cancelAvailable,
        cancelAvailableHour: cancelAvailable === 'N' ? parseInt(cancelCustomValue) || null : null,
        absenceDeductionYn: absenceDeduction,
        waitlistAutoReservationYn: waitlistAutoReservation,
        waitlistAutoReservationHour: waitlistAutoReservation === 'N' ? parseInt(waitlistAutoReservationCustomValue) || null : null,
      };

      await saveLessonSettings(data, user.token);
      toast.success(t('저장되었습니다'));
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const handleCancel = () => {
    fetchSettings();
  };

  return (
    <Box
      sx={{
        bgcolor: 'white',
        borderTop: '1px solid #D9D9D9',
        p: '32px 40px',
        mt: '20px',
      }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
        {/* 예약 가능 시간 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#262626' }}>{t('예약 가능 시간')}</Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#B3B3B3' }}>
              * 회원이 예약할 수 있는 시간을 설정합니다.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center', height: '48px' }}>
              <Radio
                checked={reservationAvailable === 'Y'}
                onChange={() => setReservationAvailable('Y')}
                value="Y"
                name="reservationAvailable"
                sx={{
                  color: '#262626',
                  '&.Mui-checked': {
                    color: '#262626',
                  },
                }}
              />
              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#262626' }}>
                {t('수업 시작 전 상시 가능')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center', height: '48px' }}>
              <Radio
                checked={reservationAvailable === 'N'}
                onChange={() => setReservationAvailable('N')}
                value="N"
                name="reservationAvailable"
                sx={{
                  color: '#262626',
                  '&.Mui-checked': {
                    color: '#262626',
                  },
                }}
              />
              {reservationAvailable === 'Y' && (
                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#262626', mr: '8px' }}>
                  {t('직접입력')}
                </Typography>
              )}
              {reservationAvailable === 'N' && (
                <>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#262626', mr: '8px' }}>
                    수업 시작
                  </Typography>
                  <TextField
                    size="small"
                    value={reservationCustomValue}
                    onChange={e => setReservationCustomValue(e.target.value)}
                    sx={{
                      width: '50px',
                      '& .MuiOutlinedInput-root': {
                        height: '36px',
                        fontSize: '14px',
                      },
                    }}
                  />
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#262626', mr: '8px' }}>
                    시간 전까지
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </Box>

        {/* 예약 취소 가능 시간 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#262626' }}>
              {t('예약 취소 가능 시간')}
            </Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#B3B3B3' }}>
              * 회원이 예약을 취소할 수 있는 시간을 설정합니다.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center', height: '48px' }}>
              <Radio
                checked={cancelAvailable === 'Y'}
                onChange={() => setCancelAvailable('Y')}
                value="Y"
                name="cancelAvailable"
                sx={{
                  color: '#262626',
                  '&.Mui-checked': {
                    color: '#262626',
                  },
                }}
              />
              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#262626' }}>
                {t('수업 시작 전 상시 가능')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center', height: '48px' }}>
              <Radio
                checked={cancelAvailable === 'N'}
                onChange={() => setCancelAvailable('N')}
                value="N"
                name="cancelAvailable"
                sx={{
                  color: '#262626',
                  '&.Mui-checked': {
                    color: '#262626',
                  },
                }}
              />
              {cancelAvailable === 'Y' && (
                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#262626', mr: '8px' }}>
                  {t('직접입력')}
                </Typography>
              )}
              {cancelAvailable === 'N' && (
                <>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#262626', mr: '8px' }}>
                    수업 시작
                  </Typography>
                  <TextField
                    size="small"
                    value={cancelCustomValue}
                    onChange={e => setCancelCustomValue(e.target.value)}
                    sx={{
                      width: '50px',
                      '& .MuiOutlinedInput-root': {
                        height: '36px',
                        fontSize: '14px',
                      },
                    }}
                  />
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#262626', mr: '8px' }}>
                    시간 전까지
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </Box>

        {/* 결석 시 수강권 차감 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#262626' }}>
              {t('결석 시 수강권 차감')}
            </Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#B3B3B3' }}>
              * 회원이 수업에 결석했을 때, 수강권 횟수를 차감할지 설정합니다.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center', height: '48px' }}>
              <Radio
                checked={absenceDeduction === 'Y'}
                onChange={() => setAbsenceDeduction('Y')}
                value="Y"
                name="absenceDeduction"
                sx={{
                  color: '#262626',
                  '&.Mui-checked': {
                    color: '#262626',
                  },
                }}
              />
              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#262626' }}>
                {t('수강권 횟수 차감')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center', height: '48px' }}>
              <Radio
                checked={absenceDeduction === 'N'}
                onChange={() => setAbsenceDeduction('N')}
                value="N"
                name="absenceDeduction"
                sx={{
                  color: '#262626',
                  '&.Mui-checked': {
                    color: '#262626',
                  },
                }}
              />
              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#262626' }}>
                {t('수강권 횟수 미차감')}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* 대기자 자동예약 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#262626' }}>{t('대기자 자동예약')}</Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#B3B3B3' }}>
              * 수업에 대기중인 회원에게 빈자리가 생기면, 자동으로 수업에 예약되게할지 설정합니다.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center', height: '48px' }}>
              <Radio
                checked={waitlistAutoReservation === 'Y'}
                onChange={() => setWaitlistAutoReservation('Y')}
                value="Y"
                name="waitlistAutoReservation"
                sx={{
                  color: '#262626',
                  '&.Mui-checked': {
                    color: '#262626',
                  },
                }}
              />
              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#262626' }}>
                {t('자동 예약 미사용')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center', height: '48px' }}>
              <Radio
                checked={waitlistAutoReservation === 'N'}
                onChange={() => setWaitlistAutoReservation('N')}
                value="N"
                name="waitlistAutoReservation"
                sx={{
                  color: '#262626',
                  '&.Mui-checked': {
                    color: '#262626',
                  },
                }}
              />
              {waitlistAutoReservation === 'Y' && (
                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#262626', mr: '8px' }}>
                  {t('직접입력')}
                </Typography>
              )}
              {waitlistAutoReservation === 'N' && (
                <>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#262626', mr: '8px' }}>
                    수업 시작
                  </Typography>
                  <TextField
                    size="small"
                    value={waitlistAutoReservationCustomValue}
                    onChange={e => setWaitlistAutoReservationCustomValue(e.target.value)}
                    sx={{
                      width: '50px',
                      '& .MuiOutlinedInput-root': {
                        height: '36px',
                        fontSize: '14px',
                      },
                    }}
                  />
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#262626', mr: '8px' }}>
                    시간 전까지
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </Box>

        {/* 버튼 */}
        <Box sx={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', mt: '8px' }}>
          <Button
            onClick={handleCancel}
            sx={{
              bgcolor: '#98A6AD',
              color: '#fff',
              height: '36px',
              width: '100px',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: 500,
              textTransform: 'uppercase',
              '&:hover': {
                bgcolor: '#7a8a94',
              },
            }}>
            {t('취소')}
          </Button>
          <Button
            onClick={handleSave}
            sx={{
              bgcolor: '#262626',
              color: '#fff',
              height: '36px',
              width: '140px',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: 500,
              textTransform: 'uppercase',
              '&:hover': {
                bgcolor: '#000000',
              },
            }}>
            {t('저장')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default GroupLessonSettings;
